import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { DocumentKind, Template } from '@xdocs/schemas';
import { Button, EmptyState, SearchInput, Skeleton, Tabs, cn, useDebouncedValue, useI18n } from '@xdocs/ui';
import { TemplateCard } from './TemplateCard.js';
import { useTemplatesList } from './useTemplatesList.js';
import { useTemplatesT } from './useTemplatesT.js';
import styles from './TemplatesPage.module.css';

type TemplateKindFilter = 'all' | DocumentKind;

const SKELETON_CARD_COUNT = 6;

/**
 * `/templates` (F1 "Start faster" gallery): a read-only, curated catalog —
 * name, kind, description, "Use template" — per FEATURES.md F1 ("searchable/
 * filterable gallery of `templates` ... Card: name, kind, description,
 * 'Use template'"). Unlike `/personas` (F16) there is no create/edit/delete:
 * `templates` has no `ownerId` (`packages/db/src/schema/documents.ts`), it's
 * a small global seeded catalog, not a user-owned collection — so no tabs
 * for `mine`/`public` either, just an optional `kind` filter alongside search.
 */
export function TemplatesPage(): ReactElement {
  const { t: tGlobal } = useI18n();
  const { t } = useTemplatesT();
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState<TemplateKindFilter>('all');
  const list = useTemplatesList(kindFilter === 'all' ? undefined : kindFilter);

  const [qDraft, setQDraft] = useState(list.query.q ?? '');
  const debouncedQ = useDebouncedValue(qDraft, 250);
  // Tracks the last `q` value WE pushed via `onQueryChange` — mirrors
  // `PersonasPage.tsx`'s identical debounce contract (E6 §5.9) so an external
  // reset (kind-filter switch resetting `useTemplatesList`'s query) can
  // resync the draft without the debounce round-trip echoing a stale value.
  const lastPushedQ = useRef(list.query.q ?? '');

  useEffect(() => {
    if (debouncedQ === (list.query.q ?? '')) return;
    const { q: _currentQ, ...rest } = list.query;
    lastPushedQ.current = debouncedQ;
    list.onQueryChange(debouncedQ ? { ...rest, q: debouncedQ, page: 1 } : { ...rest, page: 1 });
    // Only re-run when the debounced draft changes; `list.query`/
    // `onQueryChange` intentionally excluded to avoid re-firing on
    // server-driven query updates (identical to `PersonasPage.tsx`'s contract).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    const externalQ = list.query.q ?? '';
    if (externalQ !== lastPushedQ.current) {
      lastPushedQ.current = externalQ;
      setQDraft(externalQ);
    }
  }, [list.query.q]);

  function clearSearch(): void {
    setQDraft('');
  }

  function useTemplate(template: Template): void {
    // `apps/web/src/routes/generate/**` is out of this task's ownership and
    // doesn't read this param yet (no `useSearchParams`/`location.state` in
    // `GeneratePage.tsx` today) — same class of cross-boundary gap
    // `PersonasPage.tsx`'s `useInGeneration` documents for `personaId`.
    // Passing it now costs nothing (an unread query param is inert) and is
    // the hook the Generate wizard's own task can pick up to fulfil F1's
    // "Use template" pre-fill.
    void navigate(`/generate?templateId=${encodeURIComponent(template.id)}`);
  }

  const isEmpty = !list.isLoading && !list.isError && list.rows.length === 0;
  const hasQuery = Boolean(list.query.q);

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <div>
          <p className={cn(styles['eyebrow'])}>{t('templates:page.eyebrow')}</p>
          <h1 className={cn(styles['title'])}>{t('templates:page.title')}</h1>
          {/* mockup-23: design/app/23-templates.html subcopy line between H1 and the toolbar/grid — missing from a prior pass. */}
          <p className={cn(styles['subcopy'])}>{t('templates:page.subcopy')}</p>
        </div>
      </div>

      <div className={cn(styles['toolbar'])}>
        <Tabs<TemplateKindFilter>
          label={t('templates:page.title')}
          value={kindFilter}
          onChange={setKindFilter}
          items={[
            { value: 'all', label: t('templates:kind.all') },
            { value: 'book', label: t('templates:kind.book') },
            { value: 'article', label: t('templates:kind.article') },
            { value: 'report', label: t('templates:kind.report') },
          ]}
        />
        <SearchInput
          label={t('templates:list.searchLabel')}
          hideLabel
          placeholder={t('templates:list.searchPlaceholder')}
          value={qDraft}
          onChange={(event) => setQDraft(event.target.value)}
          onClear={clearSearch}
          clearLabel={tGlobal('common:action.clearFilters')}
          containerClassName={cn(styles['search'])}
        />
      </div>

      {list.isError && (
        <EmptyState kind="error" title={t('templates:list.error.title')} action={{ label: t('templates:action.retry'), onClick: list.onRetry }} />
      )}

      {!list.isError && isEmpty && !hasQuery && (
        <EmptyState
          kind="first-use"
          title={t('templates:list.empty.title')}
          description={t('templates:list.empty.description')}
          action={{ label: t('templates:list.empty.action'), onClick: () => void navigate('/generate') }}
        />
      )}

      {!list.isError && isEmpty && hasQuery && (
        <EmptyState
          kind="no-results"
          title={t('templates:list.noResults.title', { query: list.query.q ?? '' })}
          description={t('templates:list.noResults.description')}
          action={{ label: tGlobal('common:action.clearFilters'), onClick: clearSearch }}
        />
      )}

      {list.isLoading && list.rows.length === 0 && (
        <div className={cn(styles['grid'])} aria-hidden="true">
          <Skeleton shape="card" count={SKELETON_CARD_COUNT} />
        </div>
      )}

      {!list.isError && list.rows.length > 0 && (
        <div className={cn(styles['grid'])} role="list" aria-label={t('templates:page.title')}>
          {list.rows.map((template) => (
            <div role="listitem" key={template.id}>
              <TemplateCard template={template} onUseTemplate={useTemplate} />
            </div>
          ))}
        </div>
      )}

      {!list.isError && list.nextCursor && (
        <div className={cn(styles['loadMore'])}>
          <Button variant="secondary" state={list.isLoadingMore ? 'loading' : 'idle'} onClick={list.onLoadMore}>
            {tGlobal('common:action.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
